/**
 * Module dependencies.
 */

import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';
import { resolveAppUrl } from 'app/core/utils/url-resolver';

/**
 * `TrashItem` type.
 */

export type TrashItem = {
  id: string;
  name: string;
  type: 'file' | 'folder';
  deletedAt: string;
};

/**
 * Export `useNotesTrashInvalidator`.
 */

export function useNotesTrashInvalidator() {
  const queryClient = useQueryClient();

  return useCallback(async () => {
    await queryClient.invalidateQueries(['notesTrash']);
    await queryClient.invalidateQueries(['getNotesFileSystem']);
  }, [queryClient]);
}

/**
 * Export `useNotesTrash` — list soft-deleted items.
 */

export function useNotesTrash(enabled = true) {
  const client = useRequestProvider();

  return useQuery<TrashItem[]>(['notesTrash'], {
    enabled,
    queryFn: async () => client.get(resolveAppUrl('appsNotesTrash')).then(({ data }: { data: any }) => data ?? [])
  });
}

/**
 * Export `useRestoreItem`.
 */

export function useRestoreItem() {
  const client = useRequestProvider();
  const invalidate = useNotesTrashInvalidator();

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const result = await client.post(resolveAppUrl('appsNotesRestore', { interpolations: { id } }));

      await invalidate();

      return result;
    }
  });
}

/**
 * Export `usePurgeItem` — permanent delete.
 */

export function usePurgeItem() {
  const client = useRequestProvider();
  const invalidate = useNotesTrashInvalidator();

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const result = await client.delete(resolveAppUrl('appsNotesPurge', { interpolations: { id } }));

      await invalidate();

      return result;
    }
  });
}
